import { useCallback, useEffect, useRef, useState } from 'react';

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
}

/**
 * Small data-fetching hook with loading / error / retry handling.
 * Works with any async source so the demo service can be swapped for a
 * real backend without touching the components.
 */
export function useAsyncData<T>(fetcher: () => Promise<T>) {
  const [state, setState] = useState<AsyncState<T>>({
    data: null,
    loading: true,
    error: false,
  });
  const mountedRef = useRef(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true, error: false }));
    fetcherRef
      .current()
      .then((data) => {
        if (mountedRef.current) setState({ data, loading: false, error: false });
      })
      .catch(() => {
        if (mountedRef.current) setState((prev) => ({ ...prev, loading: false, error: true }));
      });
  }, []);

  useEffect(() => {
    run();
  }, [run]);

  return { ...state, refetch: run };
}